// Body selection, same rules as RecoverCore BodySelection: tap = whole group, double-tap = one muscle.
// The web body map zooms into a group instead of using double-tap: tapping a muscle there is doubleTap,
// and Done with nothing picked is confirmGroup (the whole group, like a single tap).
import { groupOf, groups, muscles, musclesIn } from './catalog.ts';

export type SelectionLevel = 'group' | 'specific';
export interface SelectedMuscle { muscle_id: string; selection: SelectionLevel }

export interface Selection {
  /** Groups selected as a whole. */
  groups: ReadonlySet<string>;
  /** Individually selected muscles (only for groups not selected as a whole). */
  specific: ReadonlySet<string>;
}

export const emptySelection: Selection = { groups: new Set(), specific: new Set() };

export function fromMuscles(list: SelectedMuscle[]): Selection {
  const g = new Set<string>();
  const s = new Set<string>();
  for (const m of list) {
    const group = groupOf(m.muscle_id);
    if (!group) continue;
    if (m.selection === 'group') g.add(group);
    else s.add(m.muscle_id);
  }
  return { groups: g, specific: new Set([...s].filter((id) => !g.has(groupOf(id)!))) };
}

const ids = (groupId: string) => musclesIn(groupId).map((m) => m.id);

export function isEmpty(sel: Selection): boolean {
  return sel.groups.size === 0 && sel.specific.size === 0;
}

/** Single tap: toggle the muscle's whole group. */
export function tap(sel: Selection, muscleId: string): Selection {
  const group = groupOf(muscleId);
  if (!group) return sel;
  const g = new Set(sel.groups);
  const s = new Set(sel.specific);
  if (g.has(group)) g.delete(group);
  else {
    g.add(group);
    ids(group).forEach((id) => s.delete(id));
  }
  return { groups: g, specific: s };
}

/** Double tap: toggle one muscle; inside a whole-group selection it narrows to just this muscle. */
export function doubleTap(sel: Selection, muscleId: string): Selection {
  const group = groupOf(muscleId);
  if (!group) return sel;
  const g = new Set(sel.groups);
  const s = new Set(sel.specific);
  if (g.has(group)) {
    g.delete(group);
    s.add(muscleId);
  } else if (s.has(muscleId)) s.delete(muscleId);
  else s.add(muscleId);
  return { groups: g, specific: s };
}

/** List toggle: switching one muscle off inside a whole-group selection keeps the others. */
export function toggleSpecific(sel: Selection, muscleId: string): Selection {
  const group = groupOf(muscleId);
  if (!group) return sel;
  const g = new Set(sel.groups);
  const s = new Set(sel.specific);
  if (g.has(group)) {
    g.delete(group);
    ids(group).filter((id) => id !== muscleId).forEach((id) => s.add(id));
  } else if (s.has(muscleId)) s.delete(muscleId);
  else s.add(muscleId);
  return { groups: g, specific: s };
}

export function setGroup(sel: Selection, groupId: string, selected: boolean): Selection {
  const g = new Set(sel.groups);
  const s = new Set(sel.specific);
  ids(groupId).forEach((id) => s.delete(id));
  if (selected) g.add(groupId);
  else g.delete(groupId);
  return { groups: g, specific: s };
}

/** Done in the zoomed group: nothing picked there means the whole group. */
export function confirmGroup(sel: Selection, groupId: string): Selection {
  return groupState(sel, groupId) === 'none' ? setGroup(sel, groupId, true) : sel;
}

export type GroupState = 'whole' | 'some' | 'none';

export function groupState(sel: Selection, groupId: string): GroupState {
  if (sel.groups.has(groupId)) return 'whole';
  return ids(groupId).some((id) => sel.specific.has(id)) ? 'some' : 'none';
}

export function isSelected(sel: Selection, muscleId: string): boolean {
  const group = groupOf(muscleId);
  return sel.specific.has(muscleId) || (!!group && sel.groups.has(group));
}

/** Rows for session_muscles, in taxonomy order. */
export function selectedMuscles(sel: Selection): SelectedMuscle[] {
  return muscles.flatMap((m): SelectedMuscle[] => {
    if (sel.groups.has(m.group)) return [{ muscle_id: m.id, selection: 'group' }];
    if (sel.specific.has(m.id)) return [{ muscle_id: m.id, selection: 'specific' }];
    return [];
  });
}

/** Short summary, e.g. "Chest, Lats, Rear delt". */
export function summary(sel: Selection): string {
  return [
    ...groups.filter((g) => sel.groups.has(g.id)).map((g) => g.name),
    ...muscles.filter((m) => sel.specific.has(m.id)).map((m) => m.name),
  ].join(', ');
}
